'use client';
import { useEffect, useRef, useState } from 'react';
import {
  canKeepCartBenefitCelebration,
  getNewlyUnlockedCartBenefit,
  type CartBenefitCelebrationSnapshot,
  type CartBenefitCelebrationTier,
} from '../../utils/cart/cartBenefits';

const CELEBRATION_DURATION_MS = 2000;

type Celebration = { id: string; expiresAt: number; snapshot: CartBenefitCelebrationSnapshot };

/**
 * The benefit to celebrate for two seconds after it unlocks, or null. `identity` names the
 * shopper and campaign; a change in it, a removed tier, or a shrinking cart ends the celebration
 * immediately rather than congratulating the wrong person.
 */
export function useCartBenefitCelebration<T extends CartBenefitCelebrationTier>(benefits: T[], identity: string) {
  const current: CartBenefitCelebrationSnapshot = { identity, benefits };
  const signature = JSON.stringify({
    identity,
    benefits: benefits.map(({ id, available, currentAmount }) => ({ id, available, currentAmount })),
  });
  const previous = useRef<CartBenefitCelebrationSnapshot | null>(null);
  const [celebration, setCelebration] = useState<Celebration | null>(null);

  useEffect(() => {
    const snapshot: CartBenefitCelebrationSnapshot = JSON.parse(signature);
    const unlocked = getNewlyUnlockedCartBenefit(previous.current, snapshot);
    previous.current = snapshot;
    setCelebration((active) => {
      if (unlocked) {
        return { id: unlocked.id, expiresAt: Date.now() + CELEBRATION_DURATION_MS, snapshot };
      }
      if (!active || !canKeepCartBenefitCelebration(active.snapshot, snapshot, active.id)) {
        return null;
      }
      return { ...active, snapshot };
    });
  }, [signature]);

  useEffect(() => {
    if (!celebration) {
      return;
    }
    const timer = setTimeout(() => setCelebration(null), Math.max(0, celebration.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [celebration]);

  // Hide stale copy in the same render that changes shopper or decreases a quantity.
  if (!celebration || !canKeepCartBenefitCelebration(celebration.snapshot, current, celebration.id)) {
    return null;
  }
  return benefits.find((benefit) => benefit.id === celebration.id) ?? null;
}
